const TRUNCATED_CLASS = "cell-truncated";
const TOGGLE_CLASS = "truncation-toggle";

/**
 * Check whether the wrapper's content actually overflows its max-height.
 * We temporarily apply the truncation class, measure, then restore.
 */
function isOverflowing(wrapper: HTMLElement): boolean {
  const hadClass = wrapper.classList.contains(TRUNCATED_CLASS);
  if (!hadClass) wrapper.classList.add(TRUNCATED_CLASS);

  const overflows = wrapper.scrollHeight > wrapper.clientHeight;

  if (!hadClass) wrapper.classList.remove(TRUNCATED_CLASS);
  return overflows;
}

export function applySecondColumnTruncation(
  dom: HTMLElement,
  contentDOM: HTMLElement
): {
  dom: HTMLElement;
  contentDOM: HTMLElement;
  update: () => boolean;
  ignoreMutation: (mutation: MutationRecord | { type: "selection"; target: Node }) => boolean;
  destroy: () => void;
} {
  // Wrapper sits between dom and contentDOM so we can toggle CSS
  // without touching contentDOM itself (avoids ProseMirror / Yjs conflicts).
  const wrapper = document.createElement("div");
  wrapper.appendChild(contentDOM);
  dom.appendChild(wrapper);

  const toggle = document.createElement("span");
  toggle.className = TOGGLE_CLASS;
  toggle.style.display = "none";
  dom.appendChild(toggle);

  let isExpanded = false;

  const applyTruncation = () => {
    if (!isOverflowing(wrapper)) {
      wrapper.classList.remove(TRUNCATED_CLASS);
      toggle.style.display = "none";
      isExpanded = false;
    } else if (!isExpanded) {
      wrapper.classList.add(TRUNCATED_CLASS);
      toggle.textContent = "Show more...";
      toggle.style.display = "";
    } else {
      // Already expanded — keep it open, just make sure toggle is visible
      toggle.style.display = "";
    }
  };

  const handleToggleClick = (e: MouseEvent) => {
    e.stopPropagation();
    isExpanded = !isExpanded;

    if (isExpanded) {
      wrapper.classList.remove(TRUNCATED_CLASS);
      toggle.textContent = "Show less";
    } else {
      wrapper.classList.add(TRUNCATED_CLASS);
      toggle.textContent = "Show more...";
    }
  };

  toggle.addEventListener("click", handleToggleClick);

  let rafId: number | null = null;
  const scheduleApply = () => {
    if (rafId !== null) return;
    rafId = window.requestAnimationFrame(() => {
      rafId = null;
      applyTruncation();
    });
  };

  // Re-evaluate truncation whenever Yjs / ProseMirror mutates contentDOM.
  const observer = new MutationObserver(scheduleApply);
  observer.observe(contentDOM, {
    childList: true,
    subtree: true,
    characterData: true
  });

  // Initial application
  scheduleApply();

  return {
    dom,
    contentDOM,
    update() {
      scheduleApply();
      return true;
    },
    ignoreMutation(mutation: MutationRecord | { type: "selection"; target: Node }) {
      const target = mutation.target as Node | null;

      // Let ProseMirror handle changes inside the editable content.
      if (target && contentDOM.contains(target)) {
        return false;
      }

      // Ignore mutations on our toggle/wrapper to avoid re-render churn.
      return true;
    },
    destroy() {
      observer.disconnect();
      toggle.removeEventListener("click", handleToggleClick);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    }
  };
}
